import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { SourcesBlock } from '../components/ReadingMeta';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import ScienceChart from '../components/ScienceChart';
import { Reveal } from '../components/Reveal';
import { PRODUCTS, METAVER_K_CHART } from '../data/science';
import { Package, ArrowRight } from 'lucide-react';

const NewchemKompetenz = () => {
  const { t, lang } = useLang();
  const c = METAVER_K_CHART;
  return (
    <div>
      <SEO title={t('kompetenz.title')} description={t('kompetenz.lead')} path="/newchem-kompetenz" lang={lang} keywords={SEO_KEYWORDS[lang]} />
      <PageHeader label={t('kompetenz.label')} title={t('kompetenz.title')} lead={t('kompetenz.lead')} imageKey="labTest2" />
      <section className="bg-white py-14 lg:py-20">
        <div className="container-wide">
          <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900">{t('kompetenz.productsTitle')}</h2>
          <p className="mt-3 text-[15px] text-slate-600 leading-relaxed max-w-3xl">{t('kompetenz.productsNote')}</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {PRODUCTS.map((p, i) => {
              const pl = p[lang];
              const specs = [
                { k: t('kompetenz.specReact'), v: pl.react },
                { k: t('kompetenz.specColour'), v: pl.colour },
                { k: t('kompetenz.specUse'), v: pl.use },
              ];
              return (
                <Reveal key={p.id} delay={(i % 3) * 90}>
                  <article data-testid={`product-card-${p.id}`} className="h-full flex flex-col rounded-xl border border-slate-200 bg-white p-7 hover:border-blue-300 hover:shadow-md hover:shadow-blue-700/5 transition">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{pl.tag}</span>
                        <h3 className="mt-1.5 text-xl font-extrabold text-slate-900 tracking-tight">{p.name}</h3>
                      </div>
                      <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700 shrink-0"><Package className="w-5 h-5" /></div>
                    </div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{pl.desc}</p>
                    <dl className="mt-5 space-y-1.5 border-t border-slate-100 pt-4">
                      {specs.map((s, si) => (
                        <div key={si} className="flex items-baseline justify-between gap-3">
                          <dt className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 shrink-0">{s.k}</dt>
                          <dd className="text-[13px] font-semibold text-slate-700 text-right">{s.v}</dd>
                        </div>
                      ))}
                    </dl>
                    <div className="mt-6">
                      <Link to="/contact" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-700 text-white text-[13px] font-semibold hover:bg-blue-800 transition-colors">
                        {t('common.requestTech')} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {/* Metaver K chart */}
          <div className="mt-14 max-w-3xl">
            <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900">{t('kompetenz.chartTitle')}</h2>
            <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t('kompetenz.chartIntro')}</p>
            <div className="mt-6">
              <Reveal>
                <ScienceChart
                  title={t('kompetenz.chartTitle')}
                  unit={c.unit[lang]}
                  categories={c.categories[lang]}
                  series={c.series.map((s) => ({ label: s.label[lang], values: s.values, color: s.color }))}
                  methodology={c.methodology[lang]}
                />
              </Reveal>
            </div>
            <div className="mt-8">
              <SourcesBlock sources={t('kompetenz.sources')} />
            </div>
          </div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default NewchemKompetenz;
